import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

import entries from 'lodash/entries';
import keyBy from 'lodash/keyBy';
import map from 'lodash/map';
import orderBy from 'lodash/orderBy';
import reduce from 'lodash/reduce';
import toNumber from 'lodash/toNumber';

import { useAuth } from '@sbv-clients/shared-feature-auth';
import { printErrorToConsole } from '@sbv-clients/ui-shared';
import { optionsToLabelMap } from '@sbv-clients/util-misc';
import {
  keyToLabel,
  mapDictionaryToOptions,
  UnwrapArray,
} from '@sbv-clients/util-misc';

import {
  Api,
  EmployeeCacheKeys,
  EntityApiApiV2EntityAccrualGroupsListGetRequest,
  EntityApiApiV2EntityBaseScheduleGroupsListGetRequest,
  EntityApiApiV2EntityEnumsEnumNameDictsGetRequest,
  EntityApiApiV2EntityEnumsEnumNameGetRequest,
  EntityApiApiV2EntityFacilityBasedLaborLevelsGetRequest,
  EntityApiApiV2EntityHolidayGroupsListGetRequest,
  EntityApiApiV2EntityLaborLevelsCheckPostRequest,
  EntityApiApiV2EntityPayRulesListGetRequest,
  EntityApiApiV2EntityScheduleStatusTypesGetRequest,
  EntityApiApiV2EntityScheduleStatusTypesListGetRequest,
  LaborLevelResponse,
  LaborLevelsLevelResponse,
  StateResponse,
  useApi,
} from '..';
import { EntityListCacheKeys } from './util/cache-keys';
import {
  buildLaborLevelOptions,
  defaultGetLaborLevelLabel,
  defaultGetLaborLevelLevelLabel,
} from './util/transform';

export const useShiftsGet = () => {
  const api = useApi();
  return useQuery({
    queryKey: [EntityListCacheKeys.Shifts],
    queryFn: () =>
      api.entityApi
        .apiV2EntityShiftsGet()
        .then((response) => response.data.data ?? {}),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};

export const useShiftsListGet = () => {
  const api = useApi();
  return useQuery({
    queryKey: [EntityListCacheKeys.ShiftList],
    queryFn: () =>
      api.entityApi
        .apiV2EntityShiftsListGet()
        .then((response) => response.data ?? []),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};

export const useEmployeeShiftTypesGet = () => {
  const api = useApi();
  return useQuery({
    queryKey: [EntityListCacheKeys.EmployeeShiftType],
    queryFn: () =>
      api.entityApi
        .apiV2EntityEmployeeShiftTypesListGet()
        .then((response) => response.data ?? {}),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};

export const useEmployeeShiftOptionsGet = () => {
  const { data, isLoading, isSuccess } = useEmployeeShiftTypesGet();
  const options = useMemo(
    () =>
      map(data, (item) => ({
        id: item.pkEmployeeShiftTypeId!,
        label: item.employeeShiftTypeId ?? '',
        value: item.pkEmployeeShiftTypeId!,
      })),
    [data]
  );
  return { options, isLoading, isSuccess };
};

const employeeInfoTextFieldsList = (api: Api) => async () => {
  const response = await api.entityApi.apiV2EntityEmployeeInfoMergeFieldsGet();
  return map(response.data, (label, key) => {
    return {
      id: key,
      label: label,
      value: key,
    };
  });
};

export const useEmployeeInfoTextFieldsList = () => {
  const api = useApi();
  return useQuery({
    queryKey: [EntityListCacheKeys.EMPLOYEE_INFO_TEXT_FIELDS_LIST],
    queryFn: employeeInfoTextFieldsList(api),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};

export const useScheduleStatusTypesWithOptionsGet = (
  body: EntityApiApiV2EntityScheduleStatusTypesListGetRequest
) => {
  const api = useApi();
  return useQuery({
    queryKey: [EntityListCacheKeys.SCHEDULE_STATUS_TYPES_WITH_OPTIONS, body],
    queryFn: () =>
      api.entityApi
        .apiV2EntityScheduleStatusTypesListGet(body)
        .then((response) => response.data),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};

export const useScheduleStatusTypeOptionsGet = (
  params: EntityApiApiV2EntityScheduleStatusTypesListGetRequest
) => {
  const {
    data: statusesWithOptions = [],
    isLoading,
    isSuccess,
  } = useScheduleStatusTypesWithOptionsGet(params);
  const options = useMemo(
    () =>
      statusesWithOptions.map((item) => ({
        id: item.pkScheduleStatusTypeId as number,
        label: item.scheduleStatusTypeId ?? '',
        value: toNumber(item.pkScheduleStatusTypeId),
        inActive: item.inActive,
      })),
    [statusesWithOptions]
  );
  const labelsByIdMap = useMemo(() => optionsToLabelMap(options), [options]);
  return { options, labelsByIdMap, isLoading, isSuccess };
};

const useBreakGroupTypesGet = () => {
  const api = useApi();
  return useQuery({
    queryKey: [EntityListCacheKeys.BREAK_GROUP_TYPES],
    queryFn: () =>
      api.entityApi
        .apiV2EntityBreakGroupsListGet()
        .then((response) => response.data ?? []),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};

export const useBreakGroupOptionsGet = () => {
  const { data, isSuccess } = useBreakGroupTypesGet();
  const options = useMemo(
    () =>
      map(data, (item) => ({
        id: item.pkBreakGroupId as number,
        label: item.breakGroupId ?? '',
        value: item.pkBreakGroupId as number,
      })),
    [data]
  );
  const labelsByIdMap = useMemo(() => optionsToLabelMap(options), [options]);
  return { options, labelsByIdMap, isSuccess };
};

export const useScheduleStatusTypesGet = (
  body?: EntityApiApiV2EntityScheduleStatusTypesGetRequest
) => {
  const api = useApi();
  return useQuery({
    queryKey: [EntityListCacheKeys.SCHEDULE_STATUS_TYPES, body],
    queryFn: () =>
      api.entityApi
        .apiV2EntityScheduleStatusTypesGet(body)
        .then((response) => response.data.data ?? {}),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};

export const useEmployeeStateList = () => {
  const api = useApi();
  return useQuery({
    queryKey: [EntityListCacheKeys.STATE_LIST],
    queryFn: () =>
      api.entityApi
        .apiV2EntityStatesGet()
        .then((response) => response.data ?? []),
    retry: false,
    refetchOnWindowFocus: false,
  });
};

export const useEmployeeStateOptionsList = ({
  valueKey = 'value',
}: {
  valueKey?: keyof StateResponse;
}) => {
  const { data, ...rest } = useEmployeeStateList();
  const options = useMemo(() => {
    return map(data, (state) => ({
      id: state.code ?? '',
      label: state.label ?? '',
      value: state[valueKey]!,
    }));
  }, [data, valueKey]);
  return { options, ...rest };
};

export const useExceptionTypesGet = () => {
  const api = useApi();
  return useQuery({
    queryKey: [EntityListCacheKeys.ExceptionTypes],
    queryFn: () =>
      api.entityApi
        .apiV2EntityExceptionTypesGet()
        .then((response) => response.data.data ?? {}),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};

export const useStandardCommentsGet = () => {
  const api = useApi();
  return useQuery({
    queryKey: [EntityListCacheKeys.StandardComment],
    queryFn: () =>
      api.entityApi
        .apiV2EntityStandardCommentsGet()
        .then((response) => response.data.data ?? {}),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};

export const usePunchTypesGet = () => {
  const api = useApi();
  return useQuery({
    queryKey: [EntityListCacheKeys.PunchTypes],
    queryFn: () =>
      api.entityApi
        .apiV2EntityEnumsPunchTypesDictsGet({ friendly: true })
        .then((response) => response.data.data ?? {}),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};

const usePunchingZonesGet = () => {
  const api = useApi();
  return useQuery({
    queryKey: [EntityListCacheKeys.PUNCHING_ZONES],
    queryFn: () =>
      api.entityApi
        .apiV2EntityPunchingZonesGet()
        .then((response) => response.data.data ?? {}),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};

export const usePunchingZoneFormOptionsGet = () => {
  const { data, isLoading, isSuccess } = usePunchingZonesGet();
  const options = useMemo(
    () =>
      mapDictionaryToOptions(data, {
        parseValue: Number,
      }),
    [data]
  );
  return { options, isLoading, isSuccess };
};

const useRelationTypesGet = () => {
  const api = useApi();
  return useQuery({
    queryKey: [EntityListCacheKeys.RELATIONSHIP_TYPES],
    queryFn: () =>
      api.entityApi
        .apiV2EntityRelationshipsGet()
        .then((response) => response.data ?? []),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};

export const useRelationshipOptionsGet = () => {
  const { data = [], isLoading, isSuccess } = useRelationTypesGet();
  const options = useMemo(
    () =>
      map(data, (value) => ({
        id: value,
        label: value,
        value: value,
      })),
    [data]
  );
  return { options, isLoading, isSuccess };
};

export const useInServiceCategoriesGet = () => {
  const api = useApi();
  return useQuery({
    queryKey: [EntityListCacheKeys.IN_SERVICE_CATEGORY_DICTIONARY_LIST],
    queryFn: () =>
      api.entityApi
        .apiV2EntityInServiceCategoriesGet()
        .then((response) => response.data ?? {}),
    retry: false,
    refetchOnWindowFocus: false,
  });
};

export const useInServiceCategoryOptionsList = () => {
  const { data, isLoading, isSuccess } = useInServiceCategoriesGet();
  const options = useMemo(
    () =>
      mapDictionaryToOptions(data, {
        parseValue: String,
      }),
    [data]
  );
  return { options, isLoading, isSuccess };
};

export const useInServiceGroupsGet = () => {
  const api = useApi();
  return useQuery({
    queryKey: [EntityListCacheKeys.IN_SERVICE_GROUP_DICTIONARY_LOST],
    queryFn: () =>
      api.entityApi
        .apiV2EntityInServiceGroupsGet()
        .then((response) => response.data ?? {}),
    retry: false,
    refetchOnWindowFocus: false,
  });
};

export const useInServiceGroupOptionsList = () => {
  const { data, isLoading, isSuccess } = useInServiceGroupsGet();
  const options = useMemo(
    () =>
      mapDictionaryToOptions(data, {
        parseValue: String,
      }),
    [data]
  );
  return { options, isLoading, isSuccess };
};

export const useHepatitisBDueTypesGet = () => {
  const api = useApi();
  return useQuery({
    queryKey: [EntityListCacheKeys.HEPATITIS_B_DUE_TYPES],
    queryFn: () =>
      api.entityApi
        .apiV2EntityHepatitisBDueGet()
        .then((response) => response.data ?? {}),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};

export const useHepatitisBReceivedTypesGet = () => {
  const api = useApi();
  return useQuery({
    queryKey: [EntityListCacheKeys.HEPATITIS_B_RECEIVED_TYPES],
    queryFn: () =>
      api.entityApi
        .apiV2EntityHepatitisBReceivedGet()
        .then((response) => response.data ?? {}),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};

const entityListLaborLevelsCheckPost =
  (api: Api) =>
  (requestBody: EntityApiApiV2EntityLaborLevelsCheckPostRequest) =>
    api.entityApi.apiV2EntityLaborLevelsCheckPost(requestBody);

export const useEmployeeLaborLevelsCheckNoEmployee = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation(entityListLaborLevelsCheckPost(api), {
    onSuccess: () => {
      queryClient
        .invalidateQueries([EntityListCacheKeys.LABOR_LEVELS_CHECK_NO_EMPLOYEE])
        .catch(printErrorToConsole);
    },
  });
};

const useReasonsGet = (
  filterBy:
    | 'isPunchReason'
    | 'isHoursReason'
    | 'isScheduleReason'
    | 'isRestrictedScheduleReason'
) => {
  const api = useApi();
  const response = useQuery({
    queryKey: [EntityListCacheKeys.REASON_TYPES],
    queryFn: () =>
      api.entityApi
        .apiV2EntityReasonsListGet()
        .then((response) => response.data ?? []),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  const getFilterData = useCallback(() => {
    if (!filterBy) {
      return response?.data ?? [];
    } else if (filterBy === 'isPunchReason') {
      return response?.data?.filter((item) => item.isPunchReason) ?? [];
    } else if (filterBy === 'isHoursReason') {
      return response?.data?.filter((item) => item.isHoursReason) ?? [];
    } else if (filterBy === 'isScheduleReason') {
      return response?.data?.filter((item) => item.isScheduleReason) ?? [];
    } else if (filterBy === 'isRestrictedScheduleReason') {
      return (
        response?.data?.filter((item) => item.isRestrictedScheduleReason) ?? []
      );
    }
    return [];
  }, [filterBy, response?.data]);

  const options = useMemo(
    () =>
      getFilterData().map((item) => ({
        id: item.reasonPkId!,
        label: item.description,
        value: toNumber(item.reasonPkId),
        meta: item,
      })),
    [getFilterData]
  );
  const labelsByIdMap = useMemo(() => optionsToLabelMap(options), [options]);
  return { ...response, options, labelsByIdMap };
};

export const usePunchReasonsOptions = () => useReasonsGet('isPunchReason');
export const useScheduleReasonsOptions = () =>
  useReasonsGet('isScheduleReason');
export const useOtherHourReasonOptions = () => useReasonsGet('isHoursReason');

export const usePayRuleTypesListGet = (
  props: EntityApiApiV2EntityPayRulesListGetRequest & { queryEnabled?: boolean }
) => {
  const api = useApi();
  const { facilityPkId } = props;
  return useQuery({
    queryKey: [EntityListCacheKeys.PAY_RULE_TYPES_LIST, { facilityPkId }],
    queryFn: () =>
      api.entityApi
        .apiV2EntityPayRulesListGet(props)
        .then((response) => response.data ?? []),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};

export const usePayRuleOptionsListGet = (
  props: EntityApiApiV2EntityPayRulesListGetRequest & {
    queryEnabled?: boolean;
    getOptionDisabled?: (item: { inactive?: boolean | null }) => boolean;
  }
) => {
  const { getOptionDisabled } = props;
  const { data, isSuccess } = usePayRuleTypesListGet(props);
  const options = useMemo(
    () =>
      map(data, (item) => ({
        id: item.pkPayRuleId as number,
        label: item.payRuleId ?? '',
        value: item.pkPayRuleId as number,
        inActive: item.inactive as boolean,
        disabled: getOptionDisabled ? getOptionDisabled(item) : item.inactive,
      })),
    [data, getOptionDisabled]
  );
  const labelsByIdMap = useMemo(() => optionsToLabelMap(options), [options]);
  return { options, labelsByIdMap, isSuccess };
};

export const useBaseScheduleGroupsFacilityIdGet = (
  body: EntityApiApiV2EntityBaseScheduleGroupsListGetRequest & {
    queryEnabled?: boolean;
  }
) => {
  const api = useApi();
  const { facilityPkId } = body;
  const { isMultiFacility } = useAuth();
  return useQuery({
    queryKey: [EntityListCacheKeys.SCHEDULE_GROUPS_FACILITY, { facilityPkId }],
    queryFn: () =>
      api.entityApi
        .apiV2EntityBaseScheduleGroupsListGet(body)
        .then((response) => response.data ?? []),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: body.queryEnabled || !(isMultiFacility && !body.facilityPkId),
  });
};

export const useAccrualGroupsFacilityIdGet = (
  body: EntityApiApiV2EntityAccrualGroupsListGetRequest & {
    queryEnabled?: boolean;
  }
) => {
  const api = useApi();
  const { facilityPkId } = body;
  const { isMultiFacility } = useAuth();
  return useQuery({
    queryKey: [EntityListCacheKeys.ACCRUAL_GROUPS_FACILITY, { facilityPkId }],
    queryFn: () =>
      api.entityApi
        .apiV2EntityAccrualGroupsListGet(body)
        .then((response) => response.data ?? []),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: body.queryEnabled || !(isMultiFacility && !body.facilityPkId),
  });
};

export const useHolidayGroupsFacilityIdGet = (
  body: EntityApiApiV2EntityHolidayGroupsListGetRequest & {
    queryEnabled?: boolean;
  }
) => {
  const api = useApi();
  const { facilityPkId } = body;
  const { isMultiFacility } = useAuth();
  return useQuery({
    queryKey: [EntityListCacheKeys.HOLIDAY_GROUPS_FACILITY, { facilityPkId }],
    queryFn: () =>
      api.entityApi
        .apiV2EntityHolidayGroupsListGet(body)
        .then((response) => response.data ?? []),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: body.queryEnabled || !(isMultiFacility && !body.facilityPkId),
  });
};

export const useAllLaborLevelsGet = () => {
  const api = useApi();
  return useQuery({
    queryKey: [EntityListCacheKeys.AllLaborLevels],
    queryFn: () =>
      api.entityApi.apiV2EntityAllLaborLevelsGet({}).then((response) => ({
        ...response.data,
        laborLevelsLevelResponse: orderBy(
          response?.data?.laborLevelsLevelResponse,
          'sortOrder',
          'asc'
        ),
      })),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};

export const useLaborLevelDesignations = () => {
  const { data, ...rest } = useAllLaborLevelsGet();
  return {
    data: data?.laborLevelDesignations,
    ...rest,
  };
};

export const useFacilityLabourLevelLevelGet = () => {
  const { data, ...rest } = useAllLaborLevelsGet();
  return {
    data: data?.facilityLaborLevelLevel,
    ...rest,
  };
};

export const useAllLaborLevelsWithOptionsGet = ({
  getLaborLevelLabel = defaultGetLaborLevelLabel,
  getLaborLevelLevelLabel = defaultGetLaborLevelLevelLabel,
}: {
  getLaborLevelLabel?: (item: LaborLevelsLevelResponse) => string;
  getLaborLevelLevelLabel?: (child: LaborLevelResponse) => string;
} = {}) => {
  const { data, isLoading, isSuccess } = useAllLaborLevelsGet();
  const options = useMemo(
    () =>
      map(data?.laborLevelsLevelResponse, (item) => {
        return {
          id: item.level!,
          label: getLaborLevelLabel(item),
          value: item.level!,
          meta: item,
          children: data?.laborLevels?.[item.level]
            ?.filter((child) => Boolean(child.laborLevelDescription))
            .map((child) => ({
              id: child.pkLaborLevelId!,
              value: child.pkLaborLevelId!,
              label: getLaborLevelLevelLabel(child),
              inActive: child.inactive,
              meta: child,
            })),
        };
      }),
    [
      data?.laborLevels,
      data?.laborLevelsLevelResponse,
      getLaborLevelLabel,
      getLaborLevelLevelLabel,
    ]
  );
  return { options, isLoading, isSuccess };
};

export const useFacilityLaborLevelLevelsGet = (
  body: EntityApiApiV2EntityFacilityBasedLaborLevelsGetRequest
) => {
  const api = useApi();
  return useQuery({
    queryKey: [EntityListCacheKeys.FacilityLaborLevelLevels, body],
    queryFn: () =>
      api.entityApi
        .apiV2EntityFacilityBasedLaborLevelsGet(body)
        .then((response) => response?.data ?? []),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};

export const useFacilityLaborLevelWithOptionsGet = (
  body: EntityApiApiV2EntityFacilityBasedLaborLevelsGetRequest,
  mappingConfig?: Pick<
    Parameters<typeof buildLaborLevelOptions>[0],
    'getLaborLevelLabel' | 'getLaborLevelLevelLabel'
  >
) => {
  const response = useAllLaborLevelsGet();
  const { data, isLoading, isSuccess } = useFacilityLaborLevelLevelsGet(body);
  const options = useMemo(
    () =>
      buildLaborLevelOptions({
        levelDefinitions: response.data?.laborLevelsLevelResponse,
        laborLevels: data,
        ...mappingConfig,
      }),
    [mappingConfig, data, response.data?.laborLevelsLevelResponse]
  );
  return { options, isLoading: isLoading || response.isLoading, isSuccess };
};

const useEnumDicQuery = ({
  params,
  queryKey,
}: {
  params: EntityApiApiV2EntityEnumsEnumNameDictsGetRequest;
  queryKey: any;
}) => {
  const api = useApi();
  return useQuery({
    queryKey,
    queryFn: () =>
      api.entityApi
        .apiV2EntityEnumsEnumNameDictsGet(params)
        .then((response) => response.data.data ?? {}),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};

export const useAI9DocumentsGet = () => {
  return useEnumDicQuery({
    queryKey: [EntityListCacheKeys.I9DocumentsA],
    params: { enumName: 'I9ListADocumentType' },
  });
};

export const useI9ListADocumentTypesFriendlyNamesGet = () => {
  const api = useApi();
  return useEnumDicQuery({
    queryKey: [EntityListCacheKeys.I9DocumentsAFriendlyNames],
    params: { enumName: 'I9ListADocumentType', friendly: true },
  });
};

export const useBI9DocumentsGet = () => {
  const api = useApi();
  return useEnumDicQuery({
    queryKey: [EntityListCacheKeys.I9DocumentsB],
    params: { enumName: 'I9ListBDocumentType' },
  });
};

export const useI9ListBDocumentTypesFriendlyNamesGet = () => {
  return useEnumDicQuery({
    queryKey: [EntityListCacheKeys.I9DocumentsBFriendlyNames],
    params: { enumName: 'I9ListBDocumentType', friendly: true },
  });
};

export const useCI9DocumentsGet = () => {
  return useEnumDicQuery({
    queryKey: [EntityListCacheKeys.I9DocumentsC],
    params: { enumName: 'I9ListCDocumentType' },
  });
};

export const useI9ListCDocumentTypesFriendlyNamesGet = () => {
  return useEnumDicQuery({
    queryKey: [EntityListCacheKeys.I9DocumentsCFriendlyNames],
    params: { enumName: 'I9ListCDocumentType', friendly: true },
  });
};

const useEnumQuery = ({
  params,
  queryKey,
}: {
  params: EntityApiApiV2EntityEnumsEnumNameGetRequest;
  queryKey: any;
}) => {
  const api = useApi();
  return useQuery({
    queryKey,
    queryFn: () =>
      api.entityApi
        .apiV2EntityEnumsEnumNameGet(params)
        .then((response) => response.data.data ?? {}),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};

export const useHoursSummaryFormatGet = () =>
  useEnumQuery({
    params: { enumName: 'hoursSummaryFormat' },
    queryKey: [EntityListCacheKeys.HOURS_SUMMARY_FORMAT],
  });

export const useEmployeeGroupingDisplayOrderTextGet = () =>
  useEnumQuery({
    params: { enumName: 'employeeGroupingDisplayOrder/texts' },
    queryKey: [EntityListCacheKeys.EMPLOYEE_GROUPING_DISPLAY_ORDER_TEXT],
  });

export const useHourTypesGet = () =>
  useEnumQuery({
    params: { enumName: 'hoursType' },
    queryKey: [EntityListCacheKeys.HoursType],
  });

export const useCommentTypesGet = () =>
  useEnumQuery({
    params: { enumName: 'commentType' },
    queryKey: [EntityListCacheKeys.CommentType],
  });

export const useHoursSummaryFormatDictionaryGet = () =>
  useEnumQuery({
    params: { enumName: 'hoursSummaryFormat' },
    queryKey: [EntityListCacheKeys.HoursSummaryFormat],
  });

export const useSchedulerTypesGet = () =>
  useEnumQuery({
    params: { enumName: 'schedulerType' },
    queryKey: [EntityListCacheKeys.SchedulerType],
  });

export const useOtherHoursPaymentStatusTypesGet = () =>
  useEnumQuery({
    params: { enumName: 'otherHoursPaymentStatusType' },
    queryKey: [EntityListCacheKeys.OtherHoursPaymentStatusType],
  });

const usePbjStatusTypesGet = () =>
  useEnumQuery({
    params: { enumName: 'pbjStatusType' },
    queryKey: [EntityListCacheKeys.PBJStatusType],
  });

export const usePbjStatusOptionsGet = () => {
  const { data, isLoading, isSuccess } = usePbjStatusTypesGet();
  const options = useMemo(
    () => mapDictionaryToOptions(data, { parseValue: Number }),
    [data]
  );
  return { options, isLoading, isSuccess };
};

export const useWorkPatternTypesGet = () =>
  useEnumQuery({
    params: { enumName: 'WorkPatternType' },
    queryKey: [EntityListCacheKeys.WorkPatternType],
  });

export const useGenderTypesGet = () =>
  useEnumQuery({
    params: { enumName: 'gender' },
    queryKey: [EntityListCacheKeys.GenderTypes],
  });

export const useRaceTypesGet = () =>
  useEnumQuery({
    params: { enumName: 'race' },
    queryKey: [EntityListCacheKeys.RaceTypes],
  });

export const useLicenseTypeList = () =>
  useEnumQuery({
    params: { enumName: 'licenseType' },
    queryKey: [EntityListCacheKeys.LicenseType],
  });

export const useInfluenzaTypesGet = () =>
  useEnumQuery({
    params: { enumName: 'influenzaType' },
    queryKey: [EntityListCacheKeys.InfluenzaType],
  });

export const useDrugScreenTypesGet = () =>
  useEnumQuery({
    params: { enumName: 'drugScreenType' },
    queryKey: [EntityListCacheKeys.DrugScreenType],
  });

const useEeocTypesGet = () => {
  return useEnumQuery({
    params: { enumName: 'eeocCategory' },
    queryKey: [EntityListCacheKeys.EEOCTypes],
  });
};

export const useEeocTypeOptionsGet = () => {
  const { data = {}, isLoading, isSuccess } = useEeocTypesGet();
  const options = useMemo(
    () =>
      mapDictionaryToOptions(data, {
        parseValue: Number,
        parseLabel: keyToLabel,
      }),
    [data]
  );
  return { options, isLoading, isSuccess };
};

const useChickenPoxHistoryTypesGet = () => {
  return useEnumQuery({
    params: { enumName: 'chickenpoxHistory' },
    queryKey: [EntityListCacheKeys.CHICKENPOX_HISTORY_TYPES],
  });
};

export const useChickenPoxHistoryTypeOptionsGet = () => {
  const { data = {}, isLoading, isSuccess } = useChickenPoxHistoryTypesGet();
  const options = useMemo(
    () =>
      mapDictionaryToOptions(data, {
        parseValue: Number,
        parseLabel: keyToLabel,
      }),
    [data]
  );
  return { options, isLoading, isSuccess };
};

const usePunchSourcesGet = () => {
  return useEnumQuery({
    params: { enumName: 'PunchSource' },
    queryKey: [EntityListCacheKeys.PunchSources],
  });
};

export const usePunchSourceOptionsGet = () => {
  const { data = {}, isLoading, isSuccess } = usePunchSourcesGet();
  const options = useMemo(
    () =>
      mapDictionaryToOptions(data, {
        parseValue: Number,
        parseLabel: keyToLabel,
      }),
    [data]
  );
  return { options, isLoading, isSuccess };
};

const useChickenPoxVaccinationTypesGet = () => {
  return useEnumQuery({
    params: { enumName: 'chickenpoxVaccine' },
    queryKey: [EntityListCacheKeys.CHICKENPOX_VACCINATION_TYPES],
  });
};

export const useChickenPoxVaccinationTypOptionsGet = () => {
  const {
    data = {},
    isLoading,
    isSuccess,
  } = useChickenPoxVaccinationTypesGet();
  const options = useMemo(
    () =>
      mapDictionaryToOptions(data, {
        parseValue: Number,
        parseLabel: keyToLabel,
      }),
    [data]
  );
  return { options, isLoading, isSuccess };
};

export const useEffectiveDateDictionaryGet = () => {
  return useEnumQuery({
    params: { enumName: 'effectiveDateType' },
    queryKey: [EntityListCacheKeys.EMPLOYEE_EFFECTIVE_DATE_DICTIONARY],
  });
};

export const useFindEmployeeLookIn = () => {
  return useEnumQuery({
    params: { enumName: 'findEmployeeLookIn' },
    queryKey: [EntityListCacheKeys.EMPLOYEES_LOOK_IN],
  });
};

export const useFindEmployeeLookInOptionsGet = () => {
  const { data = {}, isLoading, isSuccess } = useFindEmployeeLookIn();
  const options = useMemo(
    () =>
      mapDictionaryToOptions(data, {
        parseValue: Number,
        parseLabel: keyToLabel,
      }),
    [data]
  );
  return { options, isLoading, isSuccess };
};

export const useEmployeeAllStatusTypeGet = () => {
  const api = useApi();
  return useQuery({
    queryKey: [EmployeeCacheKeys.EmployeeStatusType],
    queryFn: () =>
      api.entityApi
        .apiV2EntityEmployeeStatusTypesGet()
        .then((response) => response?.data?.data ?? {}),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};

export const useShiftListOptionsGet = () => {
  const { data: shifts, isLoading, isSuccess } = useShiftsListGet();
  const options = useMemo(
    () =>
      map(shifts, (item) => ({
        id: toNumber(item.pkShiftId),
        label: item.shiftId,
        value: toNumber(item.pkShiftId),
        meta: item,
      })),
    [shifts]
  );
  const optionsByIdMap = useMemo(() => keyBy(options, 'id'), [options]);
  const labelsByIdMap = useMemo(() => optionsToLabelMap(options), [options]);
  return { options, optionsByIdMap, labelsByIdMap, isLoading, isSuccess };
};

export const usePayPeriodOptionsGet = (payPeriodGroupId?: number | null) => {
  const api = useApi();
  return useQuery({
    queryKey: [EntityListCacheKeys.PAY_PERIODS, { payPeriodGroupId }],
    queryFn: () =>
      api.entityApi
        .apiV2EntityPayPeriodsPayPeriodGroupIdListGet({
          payPeriodGroupId: payPeriodGroupId as number,
        })
        .then((response) => response.data || []),
    retry: false,
    refetchOnWindowFocus: false,
    enabled: Boolean(payPeriodGroupId),
  });
};

export type PayPeriodItemResponseType = NonNullable<
  UnwrapArray<ReturnType<typeof usePayPeriodOptionsGet>['data']>
>;

export const useReverseHourTypesGet = () => {
  const { data: hourTypesData, ...rest } = useHourTypesGet();
  const data = useMemo(
    () =>
      reduce(
        entries(hourTypesData),
        (acc: Record<string, number>, item) => {
          acc[item[1]] = +item[0];
          return acc;
        },
        {}
      ),
    [hourTypesData]
  );
  return { ...rest, data };
};

export const useStatusTypesListGet = () => {
  const api = useApi();
  return useQuery({
    queryKey: [EntityListCacheKeys.STATUS_TYPES_LIST],
    queryFn: () =>
      api.entityApi
        .apiV2EntityEmployeeStatusTypesListGet()
        .then((response) => response.data ?? []),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};

export const useStatusTypeOptions = () => {
  const { data, isLoading, isSuccess } = useStatusTypesListGet();
  const options = useMemo(
    () =>
      map(data, (item) => {
        return {
          id: item.pkEmployeeStatusTypeId as number,
          label: item.employeeStatusTypeId ?? '',
          value: item.pkEmployeeStatusTypeId as number,
          meta: item,
        };
      }),
    [data]
  );
  return { options, isLoading, isSuccess };
};

export const usePayPeriodGroupsGet = () => {
  const api = useApi();
  return useQuery({
    queryKey: [EntityListCacheKeys.PAY_PERIOD_GROUPS],
    queryFn: () =>
      api.entityApi
        .apiV2EntityPayPeriodGroupsDetailedGet()
        .then((response) => response.data ?? []),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};

export const useGetGenderOptionsGet = () => {
  const { data = {}, isLoading, isSuccess } = useGenderTypesGet();

  const options = useMemo(
    () =>
      map(data, (label, id) => ({
        id,
        label: label === 'Unknown' ? 'Other' : label,
        value: +id,
      })),
    [data]
  );
  return { options, isLoading, isSuccess };
};

export const usePayTypesGet = (facilityPkId?: number) => {
  const api = useApi();
  return useQuery({
    queryKey: [EntityListCacheKeys.PAY_TYPES, facilityPkId],
    queryFn: () =>
      api.entityApi
        .apiV2EntityPayTypesGet({ facilityPkId })
        .then((response) => response.data ?? []),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};
